import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../../../../layouts/Layout";
import * as PR from "../../../../prime-react/index";
import "./CreateReport.scss";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { getAlertDetails, getGroupsWithPermission, getSports } from "../../../../services/api";
import { fileUploadLimit, getPlainText, maxNumberOfFiles, permission,reportsFileTypesConfig,roleIds, textSanitize, editorConfig, validationMessages, usLocationIds, trimFileName } from "../../../../utils/reuse";
import { useToast } from "../../../../context/ToastContext";
import { inputSearchHandler } from "../../../../utils/inputValidate";

const CreateReport = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let fileUploadRef = useRef(null);
  const sessionData = useSelector((state) => state.auth.sessionData);
  const headers = useMemo(() => {
    return {
      sessionid: sessionData.sessionId,
      "Content-Type": "multipart/form-data",
    };
  }, [sessionData.sessionId]);

  const [regulators, setRegulators] = useState([]);
  const [usRegulators, setusRegulators] = useState([]);
  const [governingBodies, setGoverningBodies] = useState([]);
  const [sports, setSports] = useState([]);
  const { state } = useLocation();
  const [alertDetails, setAlertDetails] = useState(null);
  const [alertIdSearch, setAlertIdSearch] = useState(null);
  const [error, setError] = useState(null)
  const [alertIdloading, setAlertIdloading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    reportTitle: "",
    attachments: [],
    reportDescription: "",
    reportType: null,
    sport: null,
    alertID: "",
    regulators: [],
    usRegulators: [],
    governingBodies: [],
    enableDownload: false,
  });

  useEffect(() => {
    if(state?.formvalues) {
      setInitialValues(state?.formvalues)
    }
  }, [state?.formvalues])

  const validationSchema = Yup.object()
    .shape({
      reportTitle: Yup.string().required("Report title is required"),
      attachments: Yup.array()
        .max(maxNumberOfFiles, validationMessages.maxNumberOfFiles)
        .test('fileType', validationMessages.allFileTypesWithoutImage, (files) => {
          if (!files) return true;
          return files?.every((file) => reportsFileTypesConfig.includes(file.type))
        })
        .test('fileSize', validationMessages.sizeExceed, (files) => {
          if (!files) return true;
          return files?.every((file) => {
            return file.size <= fileUploadLimit.value
          })
        })
        .test('uniqueFileNames', validationMessages.uniqueFileNames, (value) => {
          if (!value || value.length <= 1) return true;
          const fileNames = value.map((file) => file.name);
          const uniqueFileNames = new Set(fileNames);
          return fileNames.length === uniqueFileNames.size;
        }),
      reportDescription: Yup.string().test( "valid-text", "Enter valid text", (value) =>  !(getPlainText(value) === 0 && value !== null))
      .required("Description is required"),
      reportType: Yup.object().nullable().required("Report type is required"),
      sport: Yup.object().when('reportType', {
        is: (reportType) => {
          return reportType?.name?.toLowerCase() !== "summary";
        },
        then: () => Yup.object().nullable().required("Sport is required"),
        otherwise: () => Yup.object().nullable(),
      }),
      alertID: Yup.object().when('reportType', {
        is: (reportType) => {
          return reportType?.name?.toLowerCase() === "suspicious betting";
        },
        then: () => Yup.string().nullable().required("Alert ID is required"),
        otherwise: () => Yup.string().nullable(),
      }),
      regulators: Yup.array().of(Yup.object().nullable()),
      usRegulators: Yup.array().of(Yup.object().nullable()),
      governingBodies: Yup.array().of(Yup.object().nullable()),
    }).test( "oneOfRequired", "At least one US regulator, world regulator, or governing body is required", function (values) {
    const { regulators = [], governingBodies = [], usRegulators = [] } = values || {};
    return ( (regulators?.length ?? 0) > 0 || (governingBodies?.length ?? 0) > 0 || (usRegulators?.length ?? 0) > 0 );
  }
);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit: (values) => {
      if (values.regulators?.length === 0 && values.governingBodies?.length === 0 && values.usRegulators?.length === 0) {
        formik.setErrors({...formik.errors, oneOfRequired: "At least one US regulator, world regulator, or governing body is required",});
        return;
      }
      let data = {
        title: values.reportTitle,
        description: textSanitize(values.reportDescription),
        reportType: values.reportType.name.toLowerCase(),
        receivers: [
          ...values.regulators?.map((reg) => reg.id),
          ...values.governingBodies?.map((gb) => gb.id),
          ...values.usRegulators?.map((usreg) => usreg.id),
        ],
      };
      let sportName = ''
      if (data?.reportType !== 'summary') {
        data.sportID = values.sport.id;
        sportName = values.sport.name;
      }
      if (data?.reportType === 'suspicious betting') {
        data.alertID = alertDetails?.legacyID;
      }
      let attachments = [];
      if (values.attachments?.length > 0) {
        attachments.push(...values.attachments);
      }
      if (values.enableDownload !== initialValues.enableDownload) {
        data.enableDownload = values.enableDownload;
      }
      navigate("/preview-report" , {state: {formvalues: values, reportData: data, attachments, uploadAttachements: values.attachments, sportName  }});
    }
  });

  useEffect(() => {
    getGroupsWithPermission(roleIds.regulator, permission?.reports,headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const responseData = response.data;
        const result = responseData?.length > 0 ? responseData?.sort((a, b) => a.name.localeCompare(b.name)) : [];
        const regulators = result.filter(r => !usLocationIds.includes(r.locationID));
        const usRegulators = result.filter(r => usLocationIds.includes(r.locationID));
        setRegulators(regulators);
        setusRegulators(usRegulators);
      } else {
        const error = response.error;
        showToast(error.severity, error?.errorMsg || error?.summary);
      }
    });

    getGroupsWithPermission(roleIds.governingbody,permission?.reports, headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const responseData = response?.data;
        const result = responseData?.length > 0 ? responseData?.sort((a, b) => a.name.localeCompare(b.name)) : [];
        setGoverningBodies(result);
      } else {
        const error = response.error;
        showToast(error.severity, error?.errorMsg || error?.summary);
      }
    });

    getSports(null, headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const sports = response?.data;
        setSports(sports?.length > 0 ? sports?.sort((a, b) => a.name.localeCompare(b.name)) : []);
        if (sports?.length === 0) {
          showToast("warn", "No records found");
        }
      } else {
        setSports([]);
        const error = response.error;
        showToast(error.severity, error?.errorMsg || error?.summary);
      }
    });
  }, [dispatch, headers, showToast]);

  const handleFileSelect = (event) => {
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
    const currentAttachments = formik.values.attachments || [];
    const newAttachment = event.files[0];
    const duplicateFiles = currentAttachments.some(existingFile => existingFile.name === newAttachment.name)

    if (duplicateFiles) {
      showToast("warn", validationMessages.uniqueFileNames);
      fileUploadRef.current.clear();
    } else if (currentAttachments?.length + 1 > maxNumberOfFiles) {
      showToast("warn", validationMessages.maxNumberOfFiles);
      fileUploadRef.current.clear();
    } else {
      const updatedAttachments = [...currentAttachments, newAttachment];
      formik.setFieldValue('attachments', updatedAttachments);
    }
  }

  const handleDeleteFile = (index) => {
    const updatedAttachments = [...formik.values.attachments];
    updatedAttachments?.splice(index, 1);
    formik.setFieldValue("attachments", updatedAttachments);
  };
  const type = [
    { name: "Suspicious betting" },
    { name: "Summary" },
    { name: "Others" },
  ];

  const getSearchedAlertDetails = useCallback((legacyId) => {
    getAlertDetails(legacyId, headers, dispatch, ({ result, error, data }) => {
      if (result === 'SUCCESS' && (data && data?.legacyID)) {
        setAlertDetails(data);
        setAlertIdSearch(data.legacyID);
        setError(null);
      } else {
        setError("Alert ID not found");
        setAlertDetails(null);
        setTimeout(() => {
          setError(null);
        }, 2500);
      }
    setAlertIdloading(false);
    }, { isTitle: true });
  }, [dispatch, headers]);

  const searchHandler = e => {
    e?.preventDefault();
    const normalizedSearchId = alertIdSearch?.toString().toLowerCase().trim();
    if (!normalizedSearchId || normalizedSearchId === 'alert' || normalizedSearchId === 'alert-') {
      setError("Invalid Alert ID");
      setAlertDetails(null);
      return;
    } else {
      setAlertIdloading(true);
      let legacyId = alertIdSearch;
      if (!alertIdSearch.toLowerCase().startsWith('alert-')) {
        legacyId = `alert-${alertIdSearch}`
      }
      getSearchedAlertDetails(legacyId);
    }
  }

  useEffect(() => {
    if (
      initialValues?.reportType?.name.toLowerCase() === "suspicious betting" &&
      initialValues?.alertID
    ) {
      const legacyId = initialValues?.alertID?.toLowerCase().startsWith("alert-")
        ? initialValues?.alertID
        : `alert-${initialValues?.alertID}`;

      setAlertIdloading(true);
      getSearchedAlertDetails(legacyId);
    }
  }, [getSearchedAlertDetails, initialValues]);

  return (
    <Layout backBtn={'reports'} backBtnLabel="Reports">
      <section className="news-tab news-details-section general-news-section">
        <div className="flex align-items-center justify-content-between mb-4">
          <div className="left">
            <h1>Create Report</h1>
          </div>
          <div className="right">
            <PR.Button
              label="Save & Preview"
              className="action-buttons save-button"
              onClick={formik.handleSubmit}
              disabled={formik.values?.reportType?.name?.toLowerCase() === 'suspicious betting' && (!alertDetails?.alertName || error)}
            />
          </div>
        </div>
        <form autoComplete="off" onSubmit={formik.handleSubmit}>
          <div className="grid">
            <div className="col-12">
              <div className="new-list-section">
                <div className="list-wrapper">
                  <div className="edit-details">
                    <div className="card">
                      <div className="col-12 p-0 mb-4">
                        <div className="flex flex-column form-input gap-2">
                          <label htmlFor="reportTitle">
                            Report Title <span className="text-red">*</span>
                          </label>
                          <PR.InputText
                            id="reportTitle"
                            name="reportTitle"
                            type="text"
                            className={`input-field ${
                              formik.errors.reportTitle &&
                              formik.touched.reportTitle
                                ? "is-invalid"
                                : ""
                            }`}
                            value={formik.values.reportTitle}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.errors.reportTitle &&
                            formik.touched.reportTitle && (
                              <div className="error-message">
                                {formik.errors.reportTitle}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="col-12 p-0">
                        <div className="flex flex-column gap-2">
                          <span className="label">Attach Files</span>
                          <div className="card create-file-upload flex align-items-center">
                          <PR.FileUpload
                            auto
                            mode="advanced"
                            name="attachments"
                            accept={reportsFileTypesConfig?.toString()}
                            chooseLabel="Choose File"
                            multiple
                            ref={fileUploadRef}
                            onSelect={handleFileSelect}
                            onFocus={() => formik.setFieldTouched(`attachments`)}
                            customUpload
                          />
                            <span className="ml-2">
                              {validationMessages.maxFiles}
                            </span>
                          </div>
                          {formik.values.attachments && (
                            <div className="uploaded-file-container flex flex-column gap-2">
                              {formik.values.attachments?.map((attachment, index) => (
                                <div className="uploaded-files" key={index}>
                                  {trimFileName(attachment.name)}
                                  <i className="pi pi-trash" onClick={() => handleDeleteFile(index)} />
                                </div>
                              ))}
                            </div>
                          )}
                          {formik.errors.attachments && (formik.touched.attachments || formik.submitCount > 0) && (
                            <div className="error-message">
                              {Array.isArray(formik.errors.attachments) ? (
                                Array.from(new Set(formik.errors?.attachments))?.map((error, index) => (
                                  <div key={index}>{error}</div>
                                ))
                              ) : (
                                <div>{formik.errors.attachments}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 p-0 mt-4">
                        <div className="flex flex-column gap-2">
                          <span className="label">
                            Report Description{" "}
                            <span className="text-red">*</span>
                          </span>
                          <div className="card content-editor">
                            <PR.Editor
                              id="reportDescription"
                              value={formik.values.reportDescription}
                              name="reportDescription"
                              onTextChange={(e) => {
                                formik.setFieldValue("reportDescription", e.htmlValue);
                              }}
                              modules={editorConfig.modules}
                              formats={editorConfig.formats}
                              style={{ height: "450px" }}
                            />
                          </div>
                          {formik.errors.reportDescription &&
                            formik.touched.reportDescription && (
                              <div className="error-message">
                                {formik.errors.reportDescription}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="grid mt-4">
                        <div className="col-6">
                          <div className="flex flex-column form-input gap-2">
                            <label htmlFor="reportType">
                              Report Type <span className="text-red">*</span>
                            </label>
                            <PR.Dropdown
                              inputId="reportType"
                              value={formik.values.reportType}
                              name="reportType"
                              onChange={(e) => {
                                formik.setFieldValue("reportType", e.value);
                                formik.setFieldValue("alertID", "");
                                setAlertIdSearch("");
                              }}
                              options={type}
                              optionLabel="name"
                              placeholder="Select Report type"
                              className={`w-full ${
                                formik.errors.reportType &&
                                formik.touched.reportType
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            {formik.errors.reportType &&
                              formik.touched.reportType && (
                                <div className="error-message">
                                  {formik.errors.reportType}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="col-6">
                          {formik.values?.reportType?.name?.toLowerCase() !== 'summary' &&
                            <div className="flex flex-column form-input gap-2">
                              <label htmlFor="sport">
                                Sport <span className="text-red">*</span>
                              </label>
                              <PR.Dropdown
                                inputId="sport"
                                value={formik.values.sport}
                                name="sport"
                                onChange={(e) =>
                                  formik.setFieldValue("sport", e.value)
                                }
                                filter
                                resetFilterOnHide
                                options={sports}
                                optionLabel="name"
                                placeholder="Select sport"
                                className={`w-full ${formik.errors.sport && formik.touched.sport
                                    ? "is-invalid"
                                    : ""
                                  }`}
                              />
                              {formik.errors.sport && formik.touched.sport && (
                                <div className="error-message">
                                  {formik.errors.sport}
                                </div>
                              )}
                            </div>
                          }
                        </div>
                      </div>
                      <div className="grid align-items-center mt-1">
                        <div className="col-6">
                        {formik.values?.reportType?.name?.toLowerCase() === 'suspicious betting' && (
                            <div className="flex flex-column form-input gap-2">
                              <label htmlFor="alertLegacyId">
                                Alert ID <span className="text-red">*</span>
                              </label>
                              <span className="p-input-icon-left w-12">
                                {alertIdloading
                                  ? <i className="pi pi-spin pi-spinner" />
                                  : <i className="pi pi-search"></i>
                                }
                                <PR.InputText
                                  id="alertID"
                                  name="alertID"
                                  placeholder="Enter Alert ID"
                                  className={`search-box alertID ${formik.errors.alertID && formik.touched.alertID ? "is-invalid" : ""}`}
                                  value={formik.values.alertID}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    setAlertDetails(null);
                                    const isValid = inputSearchHandler(e, setAlertIdSearch);
                                    if (isValid) {
                                      formik.setFieldValue("alertID", e.target.value);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      const isValid = inputSearchHandler(e, setAlertIdSearch);
                                      if (isValid) {
                                        searchHandler(e);
                                      }
                                    }
                                  }}
                                />
                                {error && <p className="error-message mt-1 absolute">{error}</p>}
                              </span>

                              {!alertIdSearch && formik.touched.alertID && formik.errors.alertID && (
                                <div className="error-message">{formik.errors.alertID}</div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="col-6 form-input">
                            {(alertIdSearch && alertDetails?.alertName) && (
                              <div className="alertId-msg-display">
                                <i className="pi pi-check-circle"></i> {alertDetails?.alertName}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="grid mt-4">
                        <div className="col-12 pt-0 pb-0">
                          <label htmlFor="regulators">
                            Publish and Send by email to{" "}
                            <span className="text-red">*</span>
                          </label>
                        </div>
                        <div className="col-4">
                          <div className="flex flex-column form-input gap-2">
                            <label htmlFor="usRegulators">
                             US Regulators
                            </label>
                            <PR.MultiSelect
                              inputId="usRegulators"
                              value={formik.values.usRegulators}
                              name="usRegulators"
                              onChange={(e) => {
                                formik.setFieldValue("usRegulators", e.value);
                                formik.setFieldTouched(
                                  "usRegulators",
                                  true,
                                  false
                                );
                              }}
                              options={usRegulators}
                              optionLabel="name"
                              filter
                              resetFilterOnHide
                              placeholder="Select"
                              maxSelectedLabels={3}
                              className={`w-full ${formik.errors.usRegulators &&
                                  formik.touched.usRegulators
                                  ? "is-invalid"
                                  : ""
                                }`}
                              showSelectAll
                              pt={{
                                checkboxContainer: {
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    const parent = e.target?.parentNode;
                                    if (parent && typeof parent.click === 'function') {
                                      parent.click();
                                    }
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="flex flex-column form-input gap-2">
                            <label htmlFor="regulators">
                              World Regulators
                            </label>
                            <PR.MultiSelect
                              inputId="regulators"
                              value={formik.values.regulators}
                              name="regulators"
                              onChange={(e) => {
                                formik.setFieldValue("regulators", e.value);
                                formik.setFieldTouched(
                                  "regulators",
                                  true,
                                  false
                                );
                              }}
                              options={regulators}
                              optionLabel="name"
                              filter
                              resetFilterOnHide
                              placeholder="Select"
                              maxSelectedLabels={3}
                              className={`w-full ${formik.errors.regulators &&
                                  formik.touched.regulators
                                  ? "is-invalid"
                                  : ""
                                }`}
                              showSelectAll
                              pt={{
                                checkboxContainer: {
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    const parent = e.target?.parentNode;
                                    if (parent && typeof parent.click === 'function') {
                                      parent.click();
                                    }
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                          <div className="col-4">
                            <div className="flex flex-column form-input gap-2">
                            <label htmlFor="governingBodies">
                              Sport Governing Bodies
                            </label>
                              <PR.MultiSelect
                                inputId="governingBodies"
                                value={formik.values.governingBodies}
                                onChange={(e) => {
                                  formik.setFieldValue(
                                    "governingBodies",
                                    e.value
                                  );
                                  formik.setFieldTouched("governingBodies", true);
                                }}
                                options={governingBodies}
                                optionLabel="name"
                                filter
                                resetFilterOnHide
                                placeholder="Select"
                                maxSelectedLabels={3}
                                className={`w-full ${formik.errors.governingBodies &&
                                    formik.touched.governingBodies
                                    ? "is-invalid"
                                    : ""
                                  }`}
                                showSelectAll
                                pt={{
                                  checkboxContainer: {
                                    onClick: (e) => {
                                      e.stopPropagation();
                                      const parent = e.target?.parentNode;
                                      if (parent && typeof parent.click === 'function') {
                                        parent.click();
                                      }
                                    },
                                  },
                                }}
                              />
                            </div>
                          </div>

                        {formik.errors.oneOfRequired &&
                          (formik.touched.regulators || formik.touched.usRegulators ||
                            formik.touched.governingBodies) && (
                            <div className="col-12 error-message">
                              {formik.errors.oneOfRequired}
                            </div>
                          )}
                      </div>
                      {(formik.values.usRegulators?.length > 0) && 
                        <div className="all-users-section mt-4">
                          <div className="switch-button flex gap-2 align-items-center">
                            <PR.InputSwitch id="enableDownload" name="enableDownload" checked={formik.values.enableDownload} onChange={(e) => formik.setFieldValue("enableDownload", e.value)} />
                            <label htmlFor="enableDownload">Enable Report Download</label>
                          </div>
                      </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="text-right mt-4">
            <PR.Button
              label="Save & Preview"
              className="action-buttons save-button"
              type="submit"
              disabled={formik.values?.reportType?.name?.toLowerCase() === 'suspicious betting' && (!alertDetails?.alertName || error)}
            />
          </div>
        </form>
      </section>
    </Layout>
  );
};

export default CreateReport;
